import React, { useCallback, useState } from 'react';
import { Stack, ControlLink, Text, Overlay, LoadingBalls } from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { RestAddress, useAddressesQuery } from '../../../hooks/useAddress';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { AmazonShippingAddressPreview } from '../AmazonShippingAddressPreview';
import { AmazonPaymentMethodPreview } from '../AmazonPaymentMethodPreview';

import './AmazonShippingAndPaymentPreview.scss';

type Props = {
  address?: RestAddress;
  addressId?: number | null;
  readOnly?: boolean;
  isSaving?: boolean;
  onEdit?: () => void;
  onSwitchToCard?: () => Promise<void>;
};

enum Mode {
  Resting,
  Switching
}

export const AmazonShippingAndPaymentPreview: React.FC<Props> = ({
  address,
  addressId,
  readOnly,
  isSaving = false,
  onEdit,
  onSwitchToCard
}) => {
  const { showErrorModal } = useErrorModal();
  const { addresses, error: addressesError } = useAddressesQuery({ skip: address != null });
  const [mode, setMode] = useState(Mode.Resting);

  const handleSwitchToCard = useCallback(async () => {
    setMode(Mode.Switching);
    try {
      await onSwitchToCard?.();
    } catch (error) {
      reportError('AMAZON_PAY_SWITCH_TO_CARD', error);
      showErrorModal();
    }
    setMode(Mode.Resting);
  }, [setMode, onSwitchToCard, showErrorModal]);

  const selectedAddress = address || addresses.find(address => address.id === String(addressId));

  if (addressesError) throwQueryError(addressesError);

  if (!selectedAddress) return null;

  return (
    <>
      <div className="AmazonShippingAndPaymentPreview">
        <div className="AmazonShippingAndPaymentPreview__section">
          <AmazonShippingAddressPreview address={selectedAddress} />
        </div>
        <div className="AmazonShippingAndPaymentPreview__section">
          <AmazonPaymentMethodPreview />
        </div>
        {!readOnly && (
          <Stack
            className="AmazonShippingAndPaymentPreview__actions"
            space={4}
            direction="horizontal"
            justifyContent="flex-end"
          >
            <ControlLink onClick={handleSwitchToCard}>I do not want to pay with Amazon</ControlLink>

            <ControlLink onClick={onEdit}>Edit Shipping Address</ControlLink>
          </Stack>
        )}
      </div>

      <Overlay show={mode === Mode.Switching || isSaving}>
        <LoadingBalls />

        <Text treatment="eyebrow">{isSaving ? 'Saving' : 'Switching'}</Text>
      </Overlay>
    </>
  );
};
